import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";
import SubscriptionsAPI from "../../services/SubscriptionsAPI";
import AccountAPI from "../../services/AccountAPI";
import StripePaymentAPI from "../../services/StripePaymentAPI";
import { getCookie } from "../../utils/CookieUtil";
import { useSubscription } from "../../context/SubscriptionContext";
import { useTranslation } from "../../../../i18n/LanguageContext";

/**
 * useSubscriptionActions — Custom hook that provides all subscription action handlers.
 *
 * This hook centralizes the business logic for subscription management in the UI.
 * It handles the full subscription lifecycle:
 *
 *   ┌────────────────────────────────────────────────────────────┐
 *   │  Browse Plans → Select Plan → Validate → Subscribe/Change    │
 *   │  Cancel Subscription → Undo Cancel (Resubscribe)             │
 *   │  Schedule Downgrade → Cancel Pending Downgrade               │
 *   └────────────────────────────────────────────────────────────┘
 *
 * @param {Function} fetchSubscriptionData - Callback to refresh subscription data from the server
 * @param {Object}   currentServicePlan    - The currently active service plan details
 * @param {Function} onPaymentRequired     - Optional callback when a payment method is needed
 *
 * @returns {Object} Action handlers and UI state for the Subscriptions component
 */
export const useSubscriptionActions = (
  fetchSubscriptionData,
  currentServicePlan,
  onPaymentRequired,
) => {
  // ── Global subscription context (shared across the app) ──
  const { canDowngradeTo, refreshSubscription, priceInfo } = useSubscription();
  const { t } = useTranslation();

  // ── Local UI state ──
  const [showServicePlans, setShowServicePlans] = useState(false); // Controls the plan browser modal
  const [selectedPlan, setSelectedPlan] = useState(null); // Plan the user picked but hasn't confirmed
  const [subscribing, setSubscribing] = useState(false); // Loading flag for any async action
  const [subscriptionError, setSubscriptionError] = useState(null); // Inline error banner content
  const [showPlanChangeConfirm, setShowPlanChangeConfirm] = useState(false); // Plan change confirmation modal
  const [showCancelConfirm, setShowCancelConfirm] = useState(false); // Cancel confirmation modal
  const [showConvertTrialConfirm, setShowConvertTrialConfirm] = useState(false); // Convert trial confirmation modal
  const [pendingCancellation, setPendingCancellation] = useState(null); // Tracks in-progress cancellation
  const [planChangeDetails, setPlanChangeDetails] = useState(null); // Details for the confirmation modal

  // ═══════════════════════════════════════════════════════════════════
  // CHECKOUT DRAFT HELPERS
  // Persist/restore the user's in-progress plan selection on the server
  // so it survives page refreshes and section switches.
  // ═══════════════════════════════════════════════════════════════════

  /** Map a server-side draft object to the selectedPlan shape used by the UI */
  const mapDraftToSelectedPlan = (draft) => {
    if (!draft || !draft.planId) return null;
    return {
      plan_id: draft.planId,
      plan_name: draft.planName,
      display_name:
        draft.planDisplayName || draft.planName || `Plan ${draft.planId}`,
      price_monthly: Number(draft.planPriceMonthly || 0),
      max_users: draft.planMaxUsers,
    };
  };

  /** Save the selected plan as a server-side draft so it persists across navigation */
  const saveCheckoutDraftForPlan = async (plan, metadata = {}) => {
    if (!plan?.plan_id) return;
    try {
      await SubscriptionsAPI.saveCheckoutDraft({
        planId: plan.plan_id,
        planName: plan.plan_name || plan.display_name,
        planDisplayName: plan.display_name,
        planPriceMonthly: plan.price_monthly,
        planMaxUsers: plan.max_users,
        isTrial: metadata.isTrial ?? false,
        autoRenew: metadata.autoRenew ?? true,
        paymentCycle: metadata.paymentCycle || "MONTHLY",
        promoCode: metadata.promoCode || null,
      });
      window.dispatchEvent(new CustomEvent("checkoutDraftUpdated"));
    } catch (error) {
      console.warn("⚠️ Unable to save checkout draft:", error);
    }
  };

  /** Clear the server-side checkout draft (called after successful subscription) */
  const clearCheckoutDraft = async () => {
    try {
      await SubscriptionsAPI.clearCheckoutDraft();
      window.dispatchEvent(new CustomEvent("checkoutDraftUpdated"));
    } catch (error) {
      console.warn("⚠️ Unable to clear checkout draft:", error);
    }
  };

  /** On mount, restore any previously saved checkout draft */
  useEffect(() => {
    const loadCheckoutDraft = async () => {
      try {
        const response = await SubscriptionsAPI.getCheckoutDraft();
        if (
          response?.success &&
          (response?.hasDraft || response?.data?.planId)
        ) {
          const draftPlan = mapDraftToSelectedPlan(response.data);
          if (draftPlan) {
            console.log("🧾 Restoring saved checkout draft plan:", draftPlan);
            setSelectedPlan(draftPlan);
          }
        }
      } catch (error) {
        console.warn("⚠️ Unable to load checkout draft:", error);
      }
    };

    loadCheckoutDraft();
  }, []);

  // ═══════════════════════════════════════════════════════════════════
  // PLAN BROWSING & SELECTION
  // Handlers for opening/closing the plan browser modal and
  // selecting a plan from it.
  // ═══════════════════════════════════════════════════════════════════

  const handleViewPlans = () => {
    setShowServicePlans(true);
  };

  const handleCloseServicePlans = () => {
    console.log("❌ User closed ServicePlans modal");
    setShowServicePlans(false);
  };

  // ═══════════════════════════════════════════════════════════════════
  // PLAN VALIDATION
  // Before subscribing, verify the account's current user count
  // doesn't exceed the target plan's max_users limit.
  // ═══════════════════════════════════════════════════════════════════

  // Validate if user can subscribe to the selected plan
  const validatePlanSelection = async (plan, currentSubscription) => {
    try {
      console.log("🔍 Validating plan selection:", plan.display_name);

      // Clear any previous errors when validating a new plan
      setSubscriptionError(null);

      // Get account information
      const accountResponse = await AccountAPI.getAccountByUserUuid();
      if (!accountResponse?.success || !accountResponse?.data) {
        throw new Error("Failed to retrieve account information");
      }

      const account = accountResponse.data;

      // Get current user count
      const userCountResponse = await AccountAPI.getUserCountByAccountId(
        account.accountId,
      );
      const currentUserCount = userCountResponse?.userCount || 0;

      // Get selected plan's user limit
      const selectedPlanMaxUsers = plan.max_users || plan.maxUsers || 0;

      const isChangingPlan =
        currentSubscription &&
        currentSubscription.status?.toLowerCase() === "active";

      console.log("📊 Plan validation check:", {
        currentUserCount,
        selectedPlanMaxUsers,
        planName: plan.display_name,
        isChangingPlan,
      });

      // Check if account already exceeds the selected plan's user limit
      if (currentUserCount > selectedPlanMaxUsers) {
        const errorMessage = {
          title: isChangingPlan
            ? "Cannot Change to This Plan"
            : "Cannot Subscribe to This Plan",
          message: `Your account currently has ${currentUserCount} user${
            currentUserCount !== 1 ? "s" : ""
          }, but ${plan.display_name} only allows ${selectedPlanMaxUsers} user${
            selectedPlanMaxUsers !== 1 ? "s" : ""
          }.`,
          action: `Please remove ${
            currentUserCount - selectedPlanMaxUsers
          } user${
            currentUserCount - selectedPlanMaxUsers !== 1 ? "s" : ""
          } before ${
            isChangingPlan ? "changing to" : "subscribing to"
          } this plan, or select a plan that supports more users.`,
          type: "user_limit_exceeded",
        };
        console.error(
          "❌ Plan validation failed - user limit exceeded:",
          errorMessage,
        );
        setSubscriptionError(errorMessage);
        return false;
      }

      console.log("✅ Plan validation passed");
      return true;
    } catch (error) {
      console.error("💥 Error validating plan selection:", error);
      setSubscriptionError({
        title: "Validation Error",
        message: "Failed to validate plan selection. Please try again.",
        type: "validation_error",
      });
      return false;
    }
  };

  const handlePlanSelectedFromModal = async (selectedPlanFromModal) => {
    console.log(
      "📝 Plan selected from ServicePlans modal:",
      selectedPlanFromModal,
    );

    // Clear previous error when selecting a new plan
    setSubscriptionError(null);

    setSelectedPlan(selectedPlanFromModal);
    setShowServicePlans(false);
    await saveCheckoutDraftForPlan(selectedPlanFromModal);

    // Refresh subscription data when a plan is selected
    console.log("🔄 Refreshing subscription data after plan selection");
    await fetchSubscriptionData();

    // Get current subscription to pass to validation
    // We need to fetch the latest subscription data first
    try {
      const response = await SubscriptionsAPI.getCurrentSubscription();
      const currentSubscription = response?.data || null;

      // Validate the selected plan immediately
      await validatePlanSelection(selectedPlanFromModal, currentSubscription);
    } catch (error) {
      console.error("💥 Error fetching subscription for validation:", error);
      // Don't block plan selection if we can't fetch subscription
    }
  };

  // ═══════════════════════════════════════════════════════════════════
  // SUBSCRIBE / PLAN CHANGE
  // Entry point: handleSubscribeToPlan (validates, then shows
  // confirmation modal for plan changes or calls executeSubscription
  // directly for new subscriptions).
  //
  // executeSubscription handles two paths:
  //   • Paid plan change → PUT to update Stripe subscription with proration
  //   • New sub / trial change → POST to create a new subscription
  // ═══════════════════════════════════════════════════════════════════

  const handleSubscribeToPlan = async (
    currentSubscription,
    startWithTrial = false,
    autoRenew = true,
    promoCode = null,
    discountedPrice = null,
  ) => {
    const isChangingPlan =
      currentSubscription &&
      currentSubscription.status?.toLowerCase() === "active";
    const actionType = isChangingPlan ? "plan change" : "new subscription";

    console.log(
      `💳 User initiated ${actionType} for plan:`,
      selectedPlan?.display_name,
      `with trial: ${startWithTrial}, autoRenew: ${autoRenew}`,
      promoCode ? `, promoCode: ${promoCode}` : "",
    );

    if (!selectedPlan) {
      toast.error(t("subscriptionActions.toast.selectPlanFirst"));
      return;
    }

    // Validate the plan before proceeding
    const isValid = await validatePlanSelection(
      selectedPlan,
      currentSubscription,
    );
    if (!isValid) {
      // Validation failed, error message is already set
      return;
    }

    // Show confirmation dialog for plan changes
    if (isChangingPlan) {
      // Store details for confirmation modal
      setPlanChangeDetails({
        isTrial: currentSubscription.isTrial,
        currentPlan:
          currentServicePlan?.displayName ||
          currentSubscription.planDisplayName ||
          currentSubscription.planName ||
          "Basic",
        newPlan: selectedPlan.display_name,
        currentPrice: currentSubscription.amount,
        currentBasePrice:
          priceInfo?.basePrice ||
          currentServicePlan?.priceMonthly ||
          currentSubscription.amount,
        newPrice: selectedPlan.price_monthly,
        currentCycle: currentSubscription.paymentCycle || "Monthly",
        currentUsers:
          currentServicePlan?.maxUsers || currentSubscription.maxUsers || 1,
        newUsers: selectedPlan.max_users,
        promoCode: promoCode || null,
        discountedPrice: discountedPrice,
        startWithTrial,
        autoRenew,
      });
      setShowPlanChangeConfirm(true);
      setSubscribing(false);
      return;
    }

    // If not changing plan, proceed with subscription
    await executeSubscription(
      currentSubscription,
      startWithTrial,
      autoRenew,
      promoCode,
    );
  };

  // Separate function to execute the actual subscription
  const executeSubscription = async (
    currentSubscription,
    startWithTrial = false,
    autoRenew = true,
    promoCode = null,
  ) => {
    const isChangingPlan =
      currentSubscription &&
      currentSubscription.status?.toLowerCase() === "active";
    const isChangingPlanDuringTrial =
      isChangingPlan && currentSubscription?.isTrial;
    // Paid plan change = existing paid (non-trial) subscription → use PUT to get proration
    const isPaidPlanChange = isChangingPlan && !currentSubscription?.isTrial;
    const actionType = isChangingPlan ? "plan change" : "new subscription";

    console.log("⏳ Setting subscribing state to true");
    setSubscribing(true);

    try {
      const uuid = getCookie("uuid");
      if (!uuid) {
        throw new Error("User not authenticated");
      }

      // ── Pre-flight: ensure a default payment method exists for paid subscriptions ──
      // Instead of letting the backend return a 402, catch the missing-payment-method
      // case on the client side so we can save the selected plan as a draft and
      // smoothly redirect the user to add a card first.
      //
      // Skip this check entirely for zero-cost plans (e.g. the
      // EVENT_DEMO_FULL_ACCESS plan used by full-access event demo seats).
      // Free plans never capture payment, so requiring a card would wrongly
      // block demo users with "Please add a payment method to activate your plan."
      const isFreePlan = Number(selectedPlan?.price_monthly) === 0;
      if (!isPaidPlanChange && !startWithTrial && !isFreePlan) {
        try {
          const methods = await StripePaymentAPI.getPaymentMethods();
          const hasDefault =
            Array.isArray(methods) && methods.some((m) => m.isDefault);
          if (!hasDefault) {
            console.log(
              "💳 No default payment method found — saving draft and redirecting to payment.",
            );
            // Persist the selected plan so it survives the section switch
            await saveCheckoutDraftForPlan(selectedPlan, {
              isTrial: startWithTrial,
              autoRenew,
              promoCode,
              paymentCycle: "MONTHLY",
            });

            if (typeof onPaymentRequired === "function") {
              onPaymentRequired();
            }

            toast(t("subscriptionActions.toast.addPaymentToActivate"), {
              icon: "💳",
            });
            setSubscribing(false);
            return;
          }
        } catch (pmError) {
          console.warn(
            "⚠️ Could not verify payment methods, proceeding with API call:",
            pmError,
          );
          // Fall through — let the backend decide
        }
      }

      let response;

      if (isPaidPlanChange) {
        // ===== PAID PLAN CHANGE: Use PUT /user/current =====
        // This updates the existing Stripe Subscription in-place with proration,
        // so the user gets credit for unused time on the old plan.
        // Subscription is resolved server-side from auth token.
        const updateData = {
          planId: selectedPlan.plan_id,
          planName: selectedPlan.plan_name || selectedPlan.display_name,
        };

        console.log(
          `📡 Calling SubscriptionsAPI.updateSubscription (PUT) for current subscription...`,
        );
        console.log("📝 Update data to be sent:", updateData);
        response = await SubscriptionsAPI.updateSubscription(updateData);
        console.log("📥 Subscription update API response:", response);
      } else {
        // ===== NEW SUBSCRIPTION or TRIAL PLAN CHANGE: Use POST =====
        await saveCheckoutDraftForPlan(selectedPlan, {
          isTrial: startWithTrial,
          autoRenew,
          promoCode,
          paymentCycle: "MONTHLY",
        });

        const subscriptionData = {
          planId: selectedPlan.plan_id,
          startDate: new Date().toISOString(),
          paymentCycle: "Monthly",
          autoRenew: autoRenew,
          isTrial: startWithTrial,
          // Flag to tell backend to preserve trial end date during plan change
          preserveTrialEndDate: isChangingPlanDuringTrial && startWithTrial,
          // Promotion code (if user applied one)
          promoCode: promoCode || null,
        };

        // Handle trial end date
        if (startWithTrial) {
          // IMPORTANT: If changing plan during existing trial, PRESERVE the original trial end date
          if (isChangingPlanDuringTrial && currentSubscription?.trialEndDate) {
            // Use the EXISTING trial end date - don't extend!
            subscriptionData.trialEndDate = currentSubscription.trialEndDate;
            subscriptionData.endDate = currentSubscription.trialEndDate;
            console.log(
              "📅 Preserving existing trial end date:",
              currentSubscription.trialEndDate,
              "(NOT extending trial during plan change)",
            );
          } else {
            // New trial - set trial_end_date to 30 days from now
            const trialEndDate = new Date();
            trialEndDate.setDate(trialEndDate.getDate() + 30);
            const trialEndDateStr = trialEndDate.toISOString().split("T")[0];
            subscriptionData.trialEndDate = trialEndDateStr;
            subscriptionData.endDate = trialEndDateStr;
            console.log(
              "📅 New trial period: 30 days, ending on",
              trialEndDateStr,
            );
          }
        }
        // Note: Removed the automatic trial assignment based on plan.trial_period_days
        // Trial should ONLY be started if user explicitly selects it

        console.log("📡 Calling SubscriptionsAPI.createSubscription...");
        console.log("📝 Subscription data to be sent:", subscriptionData);
        response = await SubscriptionsAPI.createSubscription(subscriptionData);
        console.log("📥 Subscription creation API response:", response);
      }

      if (response && response.success) {
        console.log(`✅ ${actionType} completed successfully!`);
        const successMessage = isChangingPlan
          ? t("subscriptionActions.toast.planChanged").replace(
              "{plan}",
              selectedPlan.display_name,
            )
          : t("subscriptionActions.toast.planActivated").replace(
              "{plan}",
              selectedPlan.display_name,
            );
        toast.success(successMessage, { duration: 4000 });
        console.log("🧹 Clearing selected plan after successful subscription");
        setSelectedPlan(null);
        setSubscriptionError(null);
        await clearCheckoutDraft();
        console.log(
          `🔄 Refreshing subscription data after successful ${actionType}`,
        );
        fetchSubscriptionData();

        // Directly refresh the global SubscriptionContext so MessageBar,
        // SidePanel, and AccountSetupJourney pick up the new subscription
        console.log(
          "🔄 Refreshing SubscriptionContext directly after subscription change",
        );
        await refreshSubscription();

        // Also dispatch custom event for any other listeners
        console.log(
          "📢 Dispatching subscriptionUpdated event to refresh MessageBar",
        );
        window.dispatchEvent(
          new CustomEvent("subscriptionUpdated", {
            detail: {
              newSubscription: response.data,
              planName: selectedPlan.display_name,
              action: isChangingPlan ? "plan_change" : "subscribe",
            },
          }),
        );
      } else {
        console.error(`❌ ${actionType} failed:`, response);
        const errorMessage = isChangingPlan
          ? t("subscriptionActions.toast.changePlanFailed")
          : t("subscriptionActions.toast.activatePlanFailed");
        toast.error(errorMessage);
      }
    } catch (error) {
      console.error(`💥 Error during ${actionType}:`, error);
      console.error("Error details:", {
        message: error.message,
        stack: error.stack,
        name: error.name,
      });

      if (error?.response?.status === 402) {
        const paymentRequiredMessage =
          error?.response?.data?.message ||
          "A payment method is required to complete this subscription.";
        setSubscriptionError({
          title: "Payment Method Required",
          message: paymentRequiredMessage,
          action:
            "Add a payment method to continue, then activate your selected plan.",
          type: "payment_required",
        });

        if (typeof onPaymentRequired === "function") {
          onPaymentRequired();
        }

        toast.error(t("subscriptionActions.toast.addPaymentToSetup"));
        return;
      }

      const errorMessage = isChangingPlan
        ? t("subscriptionActions.toast.changePlanFailed")
        : t("subscriptionActions.toast.activatePlanFailed");
      toast.error(errorMessage);
    } finally {
      console.log(
        `✅ ${actionType} process completed, setting subscribing to false`,
      );
      setSubscribing(false);
    }
  };

  const handleClearSelectedPlan = () => {
    console.log("🧹 User cleared selected plan:", selectedPlan?.display_name);
    setSelectedPlan(null);
    // Clear any error messages when canceling selection
    setSubscriptionError(null);
    clearCheckoutDraft();
  };

  // ═══════════════════════════════════════════════════════════════════
  // CONVERT TRIAL TO FULL SUBSCRIPTION
  // Keeps the same plan but converts from trial to paid.
  // ═══════════════════════════════════════════════════════════════════

  const handleConvertTrialToPaid = () => {
    console.log("🔄 User initiated trial-to-paid conversion");
    setShowConvertTrialConfirm(true);
  };

  const cancelConvertTrialConfirm = () => {
    setShowConvertTrialConfirm(false);
  };

  const executeConvertTrialToPaid = async (
    currentSubscription,
    promoCode = null,
  ) => {
    setShowConvertTrialConfirm(false);
    setSubscribing(true);

    try {
      const uuid = getCookie("uuid");
      if (!uuid) {
        throw new Error("User not authenticated");
      }

      // Pre-flight: ensure a default payment method exists
      try {
        const methods = await StripePaymentAPI.getPaymentMethods();
        const hasDefault =
          Array.isArray(methods) && methods.some((m) => m.isDefault);
        if (!hasDefault) {
          console.log(
            "💳 No default payment method found — redirecting to payment.",
          );
          if (typeof onPaymentRequired === "function") {
            onPaymentRequired();
          }
          toast(t("subscriptionActions.toast.addPaymentToConvert"), {
            icon: "💳",
          });
          setSubscribing(false);
          return;
        }
      } catch (pmError) {
        console.warn(
          "⚠️ Could not verify payment methods, proceeding:",
          pmError,
        );
      }

      const subscriptionData = {
        planId: currentSubscription.planId,
        paymentCycle: currentSubscription.paymentCycle || "Monthly",
        autoRenew: currentSubscription.autoRenew,
        isTrial: false,
        planName:
          currentSubscription.planName ||
          currentSubscription.planDisplayName ||
          currentServicePlan?.displayName,
        promoCode: promoCode || null,
      };

      console.log(
        "📡 Converting trial to paid subscription...",
        subscriptionData,
      );
      const response =
        await SubscriptionsAPI.updateSubscription(subscriptionData);
      console.log("📥 Trial conversion API response:", response);

      if (response && response.success) {
        const planName =
          currentServicePlan?.displayName ||
          currentSubscription.planDisplayName ||
          currentSubscription.planName ||
          "your plan";
        toast.success(
          t("subscriptionActions.toast.trialConverted").replace(
            "{plan}",
            planName,
          ),
          { duration: 4000 },
        );
        setSubscriptionError(null);
        setSelectedPlan(null);
        await clearCheckoutDraft();
        fetchSubscriptionData();
        await refreshSubscription();
        window.dispatchEvent(
          new CustomEvent("subscriptionUpdated", {
            detail: {
              newSubscription: response.data,
              planName,
              action: "trial_conversion",
            },
          }),
        );
      } else {
        console.error("❌ Trial conversion failed:", response);
        toast.error(t("subscriptionActions.toast.trialConvertFailed"));
      }
    } catch (error) {
      console.error("💥 Error during trial conversion:", error);

      if (error?.response?.status === 402) {
        setSubscriptionError({
          title: "Payment Method Required",
          message:
            error?.response?.data?.message ||
            "A payment method is required to convert your trial.",
          action:
            "Add a payment method to continue, then convert your trial again.",
          type: "payment_required",
        });
        if (typeof onPaymentRequired === "function") {
          onPaymentRequired();
        }
        toast.error(t("subscriptionActions.toast.addPaymentToConvert"));
        return;
      }

      toast.error(
        error?.response?.data?.message ||
          t("subscriptionActions.toast.trialConvertFailed"),
      );
    } finally {
      setSubscribing(false);
    }
  };

  // ═══════════════════════════════════════════════════════════════════
  // CANCEL SUBSCRIPTION
  // User confirms via the "Are you sure?" modal, then the cancellation
  // is executed immediately against the backend API.
  // ═══════════════════════════════════════════════════════════════════

  const handleCancelSubscription = async () => {
    console.log("🚫 User initiated subscription cancellation");

    // Show confirmation modal instead of window.confirm
    setPendingCancellation(true);
    setShowCancelConfirm(true);
  };

  const executeCancelSubscription = async (reason) => {
    console.log("✅ User confirmed subscription cancellation");

    try {
      console.log("📝 Cancellation reason provided:", reason);
      console.log("📡 Calling SubscriptionsAPI.cancelSubscription...");

      const response = await SubscriptionsAPI.cancelSubscription(
        reason || "User requested cancellation",
      );
      console.log("📥 Subscription cancellation API response:", response);

      if (response && response.success) {
        console.log("✅ Subscription cancellation scheduled successfully!");
        toast.success(
          "Subscription cancelled. You will continue to have full access until the end of your current billing period — no further charges will apply.",
          {
            duration: 6000,
          },
        );

        console.log("🔄 Refreshing subscription data after cancellation");
        fetchSubscriptionData();

        // Directly refresh the global SubscriptionContext
        console.log(
          "🔄 Refreshing SubscriptionContext directly after cancellation",
        );
        await refreshSubscription();

        // Also dispatch custom event for any other listeners
        console.log(
          "📢 Dispatching subscriptionUpdated event after cancellation",
        );
        window.dispatchEvent(
          new CustomEvent("subscriptionUpdated", {
            detail: {
              action: "cancel",
            },
          }),
        );
      } else {
        console.error("❌ Subscription cancellation failed:", response);
        toast.error(t("subscriptionActions.toast.cancelFailed"));
      }
    } catch (error) {
      console.error("💥 Error cancelling subscription:", error);
      console.error("Error details:", {
        message: error.message,
        stack: error.stack,
        name: error.name,
      });
      toast.error(t("subscriptionActions.toast.cancelFailed"));
    }
  };

  // ═══════════════════════════════════════════════════════════════════
  // PLAN CHANGE CONFIRMATION MODAL
  // Shown when an active subscriber picks a different plan.
  // confirmPlanChange() proceeds with the change;
  // cancelPlanChange() dismisses the modal.
  // ═══════════════════════════════════════════════════════════════════

  const confirmPlanChange = async () => {
    setShowPlanChangeConfirm(false);
    await executeSubscription(
      planChangeDetails
        ? {
            status: "active",
            isTrial: planChangeDetails.isTrial,
            ...planChangeDetails,
          }
        : null,
      planChangeDetails?.startWithTrial || false,
      planChangeDetails?.autoRenew ?? true,
      planChangeDetails?.promoCode || null,
    );
  };

  const cancelPlanChange = () => {
    setShowPlanChangeConfirm(false);
    setPlanChangeDetails(null);
    setSubscribing(false);
  };

  const cancelSubscriptionConfirm = () => {
    setShowCancelConfirm(false);
    setPendingCancellation(null);
  };

  // ═══════════════════════════════════════════════════════════════════
  // UNDO CANCELLATION (RESUBSCRIBE)
  // Reverses a cancellation while the subscription record still exists.
  //
  // For trial subscriptions:
  //   - Restores status from CANCELLED → ACTIVE
  //   - Re-enables autoRenew
  //   - Preserves the original trial end date (no extension)
  //   - No charge — the user continues their remaining trial time
  //
  // For paid subscriptions:
  //   - Reverses Stripe's cancel_at_period_end flag
  //   - Billing resumes normally at the next cycle
  //
  // NOTE: Once the trial expires (status → EXPIRED), this won't work.
  // The user would need to create a new paid subscription instead.
  // ═══════════════════════════════════════════════════════════════════

  const handleUndoCancelSubscription = async () => {
    console.log("↩️ User requested to undo subscription cancellation");
    try {
      setSubscribing(true);
      setSubscriptionError(null);

      const response = await SubscriptionsAPI.undoCancelSubscription();

      if (response?.success) {
        toast.success(t("subscriptionActions.toast.reactivated"), {
          duration: 4000,
        });
        await fetchSubscriptionData();
        await refreshSubscription();

        window.dispatchEvent(
          new CustomEvent("subscriptionUpdated", {
            detail: { action: "undo_cancel" },
          }),
        );
      } else {
        const msg = response?.message || "Failed to reactivate subscription";
        toast.error(msg);
      }
    } catch (error) {
      console.error("❌ Error undoing subscription cancellation:", error);
      const msg =
        error?.response?.data?.message ||
        error.message ||
        "Failed to reactivate subscription. Please try again.";
      toast.error(msg);
    } finally {
      setSubscribing(false);
    }
  };

  // ═══════════════════════════════════════════════════════════════════
  // CANCEL PENDING DOWNGRADE
  // When a user has scheduled a downgrade (e.g., Family → Individual)
  // that hasn't taken effect yet, this reverses it so the current
  // plan continues at the next billing cycle.
  // ═══════════════════════════════════════════════════════════════════

  const handleCancelDowngrade = async () => {
    console.log(
      "🔄 User requested to cancel pending downgrade for current subscription",
    );
    try {
      setSubscribing(true);
      setSubscriptionError(null);

      const response = await SubscriptionsAPI.cancelPendingDowngrade();

      if (response?.success) {
        toast.success(t("subscriptionActions.toast.downgradeCancelled"), {
          duration: 4000,
        });
        // Refresh both sources of truth
        await fetchSubscriptionData();
        await refreshSubscription();
      } else {
        const msg = response?.message || "Failed to cancel pending downgrade";
        setSubscriptionError(msg);
        toast.error(msg);
      }
    } catch (error) {
      console.error("❌ Error cancelling pending downgrade:", error);
      const msg =
        error?.response?.data?.message ||
        error.message ||
        "Failed to cancel pending downgrade. Please try again.";
      setSubscriptionError(msg);
      toast.error(msg);
    } finally {
      setSubscribing(false);
    }
  };

  // ═══════════════════════════════════════════════════════════════════
  // SCHEDULE PROMO CODE (deferred — takes effect at next billing date)
  // An existing paid subscriber enters a promo code that does NOT change
  // the current period. The discount applies at the next billing date and
  // onward until the promo expires.
  // ═══════════════════════════════════════════════════════════════════

  const handleApplyPromo = async (code) => {
    if (!code || !code.trim()) {
      toast.error(t("subscriptionActions.toast.enterPromoCode"));
      return false;
    }
    console.log("🔄 User requested to schedule promo code:", code);
    try {
      setSubscribing(true);
      setSubscriptionError(null);

      const response = await SubscriptionsAPI.applyPromoToSubscription(
        code.trim(),
      );

      if (response?.success) {
        toast.success(
          response?.message || t("subscriptionActions.toast.promoScheduled"),
          { duration: 5000 },
        );
        await fetchSubscriptionData();
        await refreshSubscription();
        return true;
      }
      const msg =
        response?.message || t("subscriptionActions.toast.promoScheduleFailed");
      setSubscriptionError(msg);
      toast.error(msg);
      return false;
    } catch (error) {
      console.error("❌ Error scheduling promo code:", error);
      const msg =
        error?.response?.data?.message ||
        error.message ||
        t("subscriptionActions.toast.promoScheduleFailed");
      setSubscriptionError(msg);
      toast.error(msg);
      return false;
    } finally {
      setSubscribing(false);
    }
  };

  // ═══════════════════════════════════════════════════════════════════
  // CANCEL SCHEDULED PROMO CODE
  // Reverses a promo code that was scheduled but has not taken effect yet.
  // ═══════════════════════════════════════════════════════════════════

  const handleCancelPendingPromo = async () => {
    console.log("🔄 User requested to cancel scheduled promo code");
    try {
      setSubscribing(true);
      setSubscriptionError(null);

      const response = await SubscriptionsAPI.cancelPendingPromo();

      if (response?.success) {
        toast.success(
          response?.message || t("subscriptionActions.toast.promoCancelled"),
          {
            duration: 4000,
          },
        );
        await fetchSubscriptionData();
        await refreshSubscription();
        return true;
      }
      const msg =
        response?.message || t("subscriptionActions.toast.promoCancelFailed");
      setSubscriptionError(msg);
      toast.error(msg);
      return false;
    } catch (error) {
      console.error("❌ Error cancelling scheduled promo code:", error);
      const msg =
        error?.response?.data?.message ||
        error.message ||
        t("subscriptionActions.toast.promoCancelFailed");
      setSubscriptionError(msg);
      toast.error(msg);
      return false;
    } finally {
      setSubscribing(false);
    }
  };

  // ═══════════════════════════════════════════════════════════════════
  // EXPORTED STATE & HANDLERS
  // Everything the Subscriptions component needs from this hook.
  // ═══════════════════════════════════════════════════════════════════

  return {
    // UI state
    showServicePlans, // Boolean: is the plan browser modal open?
    selectedPlan, // Object|null: plan user selected but hasn't confirmed
    subscribing, // Boolean: is an async action in progress?
    subscriptionError, // Object|null: inline error banner content
    showPlanChangeConfirm, // Boolean: is the plan change confirmation modal open?
    showCancelConfirm, // Boolean: is the cancel confirmation modal open?
    planChangeDetails, // Object|null: details for the plan change confirmation

    // Error management
    setSubscriptionError,

    // Plan browsing & selection
    handleViewPlans, // Open the plan browser modal
    handleCloseServicePlans, // Close the plan browser modal
    handlePlanSelectedFromModal, // User picked a plan from the modal

    // Subscribe / plan change
    handleSubscribeToPlan, // Validate & subscribe (or show confirmation for changes)
    handleClearSelectedPlan, // Clear the selected plan without subscribing

    // Cancellation & resubscription
    handleCancelSubscription, // Initiate cancellation (shows confirmation modal)
    handleUndoCancelSubscription, // Undo cancellation / resubscribe (CANCELLED → ACTIVE)
    executeCancelSubscription, // Execute the actual cancellation (after 5s undo window)
    cancelSubscriptionConfirm, // Dismiss the cancel confirmation modal

    // Downgrade management
    handleCancelDowngrade, // Cancel a pending downgrade

    // Scheduled promo code (takes effect at next billing date)
    handleApplyPromo, // Schedule a promo code for the next billing date
    handleCancelPendingPromo, // Cancel a not-yet-effective scheduled promo

    // Plan change confirmation modal
    confirmPlanChange, // Confirm the plan change
    cancelPlanChange, // Dismiss the plan change confirmation

    // Trial-to-paid conversion
    showConvertTrialConfirm, // Boolean: is the convert trial modal open?
    handleConvertTrialToPaid, // Show the convert trial confirmation modal
    executeConvertTrialToPaid, // Execute the trial-to-paid conversion
    cancelConvertTrialConfirm, // Dismiss the convert trial confirmation modal
  };
};
